import { createSelector, createSlice, type PayloadAction } from '@reduxjs/toolkit';

import type { UsersState } from './usersTypes';
import {
  fetchUserProfile,
  updateUserProfile,
  fetchOrgMembers,
  updateMemberRole,
  removeMember,
  bootstrapUserProfile,
  startPhoneVerification,
  confirmPhoneVerification,
  fetchUsers,
  updateBusinessUserOrgCreation,
} from './usersThunks';
import { logoutUser } from '@/features/auth/authThunks';
import type { IBusinessUser } from '@/domain/entities/user';
import type { RootState } from '@/store/root-state';
import { needsOnboarding as computeNeedsOnboarding } from './onboardingSelectors';

const initialState: UsersState = {
  users: [],
  profile: null,
  organizationMembers: [],
  isLoading: false,
  isLoadingMembers: false,
  hasFetched: false,
  hasFetchedUserManagement: false,
  error: null,
  bootstrappingError: null,
  needsOnboarding: false,
  isVerifyingPhone: false,
  phoneVerificationError: null,
  listingUsers: false,
  listUsersError: null,
  hasListedUsers: false,
  listUsersHasMore: true,
  listUsersNextToken: undefined,
};

function upsertUserInArray(users: IBusinessUser[], user: IBusinessUser) {
  const idx = users.findIndex((u) => u.applicationUserId === user.applicationUserId);
  if (idx >= 0) users[idx] = user;
  else users.unshift(user);
}

const usersSlice = createSlice({
  name: 'users',
  initialState,
  reducers: {
    // Set user data directly (e.g., after successful login)
    setUser: (state, action: PayloadAction<IBusinessUser>) => {
      state.profile = action.payload;
      state.error = null;
    },

    // Update specific user fields
    updateProfile: (state, action: PayloadAction<Partial<IBusinessUser>>) => {
      if (state.profile) {
        state.profile = { ...state.profile, ...action.payload };
      }
    },

    // Set needs onboarding flag
    setNeedsOnboarding: (state, action: PayloadAction<boolean>) => {
      state.needsOnboarding = action.payload;
    },

    // Mark user management data as fetched (to prevent re-fetching)
    markUserManagementFetched: (state) => {
      state.hasFetchedUserManagement = true;
    },

    // Clear user data (on logout)
    clearUser: () => {
      return { ...initialState };
    },

    resetUsersList: (state) => {
      state.users = [];
      state.listUsersNextToken = undefined;
      state.listUsersHasMore = true;
      state.listUsersError = null;
      state.hasListedUsers = false;
    },

    upsertUserInList: (state, action: PayloadAction<IBusinessUser>) => {
      upsertUserInArray(state.users, action.payload);
    },
  },
  extraReducers: (builder) => {
    builder
      // Handle fetchUserProfile
      .addCase(fetchUserProfile.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchUserProfile.fulfilled, (state, action) => {
        const profile = action.payload;
        state.isLoading = false;
        state.hasFetched = true;
        state.profile = profile;
        upsertUserInArray(state.users, profile);
        state.needsOnboarding = computeNeedsOnboarding(profile);
      })
      .addCase(fetchUserProfile.rejected, (state, action) => {
        state.isLoading = false;
        state.hasFetched = true;
        // Check if it's a 404 (needs onboarding)
        const payload = action.payload as { message?: string; needsOnboarding?: boolean } | undefined;
        if (payload?.needsOnboarding) {
          state.needsOnboarding = true;
          state.error = null;
          state.bootstrappingError = null;
        } else {
          state.bootstrappingError = payload?.message || action.error.message || 'Failed to fetch user profile';
        }
      })
      // Handle idempotent profile bootstrap
      .addCase(bootstrapUserProfile.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(bootstrapUserProfile.fulfilled, (state, action) => {
        state.isLoading = false;
        state.hasFetched = true;
        state.profile = action.payload;
        upsertUserInArray(state.users, action.payload);
        state.needsOnboarding = computeNeedsOnboarding(action.payload);
      })
      .addCase(bootstrapUserProfile.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload?.message || action.error.message || 'Failed to bootstrap user profile';
      })
      // Handle updateUserProfile
      .addCase(updateUserProfile.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(updateUserProfile.fulfilled, (state, action) => {
        state.isLoading = false;
        state.profile = action.payload.data;
        if (state.profile) state.needsOnboarding = computeNeedsOnboarding(state.profile);
      })
      .addCase(updateUserProfile.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload?.message || action.error.message || 'Failed to update user profile';
      })
      // Handle fetchOrgMembers - don't set global error for non-critical operations
      .addCase(fetchOrgMembers.pending, (state) => {
        state.isLoadingMembers = true;
      })
      .addCase(fetchOrgMembers.fulfilled, (state, action) => {
        state.isLoadingMembers = false;
        state.organizationMembers = action.payload;
      })
      .addCase(fetchOrgMembers.rejected, (state) => {
        state.isLoadingMembers = false;
        // Don't set global error - members list is not critical for page load
      })
      // Handle updateMemberRole - don't set global error for non-critical operations
      .addCase(updateMemberRole.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(updateMemberRole.fulfilled, (state, action) => {
        state.isLoading = false;
        const index = state.organizationMembers.findIndex((m) => m.id === action.payload.id);
        if (index !== -1) {
          state.organizationMembers[index] = action.payload;
        }
      })
      .addCase(updateMemberRole.rejected, (state) => {
        state.isLoading = false;
        // Don't set global error - handled locally with toast
      })
      // Handle removeMember - don't set global error for non-critical operations
      .addCase(removeMember.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(removeMember.fulfilled, (state, action) => {
        state.isLoading = false;
        state.organizationMembers = state.organizationMembers.filter((m) => m.id !== action.payload);
      })
      .addCase(removeMember.rejected, (state) => {
        state.isLoading = false;
        // Don't set global error - handled locally with toast
      })
      // Handle logout - clear all user state
      .addCase(logoutUser.fulfilled, () => {
        return { ...initialState };
      })
      // ========== PHONE VERIFICATION ==========
      .addCase(startPhoneVerification.pending, (state) => {
        state.isVerifyingPhone = true;
        state.phoneVerificationError = null;
      })
      .addCase(startPhoneVerification.fulfilled, (state) => {
        state.isVerifyingPhone = false;

        // ✅ Update profile locally (no refetch)
        if (state.profile) {
          // user is still unverified after "start"
          state.profile.phoneVerified = false;
          state.profile.updatedAt = Date.now();
        }

        // Keep onboarding flag in sync
        if (state.profile) {
          state.needsOnboarding = computeNeedsOnboarding(state.profile);
        }
      })
      .addCase(startPhoneVerification.rejected, (state, action) => {
        state.isVerifyingPhone = false;
        state.phoneVerificationError =
          action.payload?.message || action.error.message || 'Failed to send verification code';
      })
      .addCase(confirmPhoneVerification.pending, (state) => {
        state.isVerifyingPhone = true;
        state.phoneVerificationError = null;
      })
      .addCase(confirmPhoneVerification.fulfilled, (state, action) => {
        state.isVerifyingPhone = false;

        // ✅ Update profile locally (no refetch)
        if (state.profile) {
          state.profile.phoneVerified = action.payload.phoneVerified ?? true;
          state.profile.updatedAt = Date.now();

          // Keep onboarding flag in sync
          state.needsOnboarding = computeNeedsOnboarding(state.profile);
        }
      })
      .addCase(confirmPhoneVerification.rejected, (state, action) => {
        state.isVerifyingPhone = false;
        state.phoneVerificationError =
          action.payload?.message || action.error.message || 'Failed to verify phone number';
      })
      // ========== ADMIN: LIST USERS ==========
      .addCase(fetchUsers.pending, (state) => {
        state.listingUsers = true;
        state.listUsersError = null;
      })
      .addCase(fetchUsers.fulfilled, (state, action) => {
        state.listingUsers = false;
        state.hasListedUsers = true;

        const incoming = action.payload.items ?? [];

        // If we previously had a token, it means we were paging; but the safer way:
        // Decide append vs replace by checking whether we already have users AND we had a nextToken before the call.
        // Better: pass a meta flag, but we'll keep it simple:
        const isPaging = Boolean(state.listUsersNextToken); // "load more" usually uses existing token
        state.users = isPaging ? [...state.users, ...incoming] : incoming;

        state.listUsersNextToken = action.payload.nextToken;
        state.listUsersHasMore = Boolean(action.payload.nextToken);
      })
      .addCase(fetchUsers.rejected, (state, action) => {
        state.listingUsers = false;
        state.listUsersError = action.payload?.message || action.error.message || 'Failed to list users';
      })
      .addCase(updateBusinessUserOrgCreation.fulfilled, (state, action) => {
        const user = state.users.find((item) => item.applicationUserId === action.payload.applicationUserId);
        if (user) user.allowCreateOrg = action.payload.allowCreateOrg;
        if (state.profile?.applicationUserId === action.payload.applicationUserId) {
          state.profile.allowCreateOrg = action.payload.allowCreateOrg;
        }
      });
  },
});

export const { setUser, updateProfile, setNeedsOnboarding, markUserManagementFetched, clearUser } = usersSlice.actions;
export default usersSlice.reducer;

// ----------------- selectors -----------------
const selectUsersState = (state: RootState) => state.users;

export const selectUserProfileBootstrapping = createSelector(
  selectUsersState,
  (usersState) => usersState.isLoading && !usersState.hasFetched,
);

export const selectBootstrappingError = createSelector(selectUsersState, (usersState) => usersState.bootstrappingError);
export const selectProfileLoading = createSelector(selectUsersState, (usersState) => usersState.isLoading);
export const selectPhoneVerificationInProgress = createSelector(
  selectUsersState,
  (usersState) => usersState.isVerifyingPhone,
);
export const selectPhoneVerificationError = createSelector(
  selectUsersState,
  (usersState) => usersState.phoneVerificationError,
);
export const selectAllUsers = createSelector(selectUsersState, (usersState) => usersState.users);
export const selectUserById = (userId?: string) =>
  createSelector(selectUsersState, (usersState) => {
    if (!userId) return null;
    return usersState.users.find((user) => user.applicationUserId === userId.trim()) ?? null;
  });
export const selectUserByCognitoSubject = (subject?: string) =>
  createSelector(selectUsersState, (usersState) => {
    if (!subject) return null;
    return usersState.users.find((user) => user.id === subject.trim()) ?? null;
  });
export const selectListUsersError = createSelector(selectUsersState, (usersState) => usersState.listUsersError);
export const selectListUsersHasMore = createSelector(selectUsersState, (usersState) => usersState.listUsersHasMore);
export const selectListUsersLoading = createSelector(selectUsersState, (usersState) => usersState.listingUsers);
export const selectHasListedUsers = createSelector(selectUsersState, (usersState) => usersState.hasListedUsers);
export const selectListUsersNextToken = createSelector(selectUsersState, (s) => s.listUsersNextToken);
